import React, { useEffect, useState } from 'react';
import { differenceInDays, format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';

function CountdownRing({ days, totalDays }) {
  const pct = Math.max(0, Math.min(1, (totalDays - days) / totalDays));
  const r = 34, circ = 2 * Math.PI * r, offset = circ * (1 - pct);
  return (
    <svg width="80" height="80" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(250,247,242,0.08)" strokeWidth="5"/>
      <circle cx="40" cy="40" r={r} fill="none" stroke="#C4D4C0" strokeWidth="5"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 40 40)" style={{transition:'stroke-dashoffset 1s ease'}}/>
      <text x="40" y="44" textAnchor="middle" fontFamily="Cormorant Garamond,serif"
        fontSize="18" fontWeight="300" fill="#FAF7F2">{days > 999 ? '∞' : days}</text>
    </svg>
  );
}

function TaskPhase({ phase, tasks, onToggle }) {
  const [open, setOpen] = useState(phase.phase_number <= 2);
  const done = tasks.filter(t => t.is_completed).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const isActive = pct > 0 && pct < 100;
  const isDone = pct === 100;
  return (
    <div className="card" style={{marginBottom:'0.75rem'}}>
      <div onClick={() => setOpen(o => !o)} style={{padding:'1rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.85rem'}}>
          <div style={{width:'8px',height:'8px',borderRadius:'50%',background:isDone?'var(--sage)':isActive?'var(--blush)':'var(--sage-light)'}}/>
          <div>
            <div style={{fontFamily:'var(--font-display)',fontSize:'1rem',fontWeight:400}}>{phase.phase_title}</div>
            <div style={{fontSize:'0.6rem',color:'var(--warm-grey)',marginTop:'0.1rem'}}>{done}/{tasks.length} complete</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
          <span className={`pill ${isDone?'pill-done':isActive?'pill-now':'pill-upcoming'}`}>
            {isDone?'Complete':isActive?'In progress':'Upcoming'}
          </span>
          <span style={{color:'var(--warm-grey)',fontSize:'0.75rem',display:'inline-block',transform:open?'rotate(90deg)':'none',transition:'transform 0.2s'}}>›</span>
        </div>
      </div>
      {open && (
        <div style={{borderTop:'1px solid rgba(46,43,62,0.06)',padding:'0.5rem 1.25rem 0.75rem'}}>
          <div className="progress-bar" style={{marginBottom:'0.75rem'}}>
            <div className="progress-fill" style={{width:`${pct}%`}}/>
          </div>
          {tasks.map(task => (
            <div key={task.id} onClick={() => onToggle(task.id, !task.is_completed)}
              style={{display:'flex',alignItems:'flex-start',gap:'0.75rem',padding:'0.6rem 0',borderBottom:'1px solid rgba(46,43,62,0.05)',cursor:'pointer'}}>
              <div style={{width:'1.1rem',height:'1.1rem',borderRadius:'3px',border:`1.5px solid ${task.is_completed?'var(--sage)':'rgba(46,43,62,0.2)'}`,background:task.is_completed?'var(--sage)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:'0.1rem'}}>
                {task.is_completed && <span style={{color:'white',fontSize:'0.55rem'}}>✓</span>}
              </div>
              <span style={{fontSize:'0.82rem',fontWeight:300,color:task.is_completed?'var(--warm-grey)':'var(--navy)',textDecoration:task.is_completed?'line-through':'none',opacity:task.is_completed?0.6:1}}>
                {task.title}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ showToast, user, profile }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const firstName = profile?.first_name || user?.user_metadata?.first_name || 'Bride';
  const isPremium = profile?.is_premium || false;
  const rawDate = profile?.wedding_date || user?.user_metadata?.wedding_date;
  const weddingDate = rawDate ? parseISO(rawDate) : null;
  const daysLeft = weddingDate ? Math.max(0, differenceInDays(weddingDate, new Date())) : null;

  useEffect(() => {
    if (!user) return;
    supabase.from('tasks').select('*').eq('user_id', user.id)
      .order('phase_number', {ascending:true}).order('sort_order', {ascending:true})
      .then(({data}) => { setTasks(data || []); setLoading(false); });
  }, [user]);

  async function handleToggle(taskId, isCompleted) {
    setTasks(prev => prev.map(t => t.id === taskId ? {...t, is_completed: isCompleted} : t));
    await supabase.from('tasks').update({is_completed: isCompleted}).eq('id', taskId);
    if (isCompleted) showToast('Task complete! ✓');
  }

  const phases = [...new Map(tasks.map(t => [t.phase_number, {phase_number:t.phase_number,phase_title:t.phase_title}])).values()].sort((a,b) => a.phase_number - b.phase_number);
  const totalDone = tasks.filter(t => t.is_completed).length;
  const overallPct = tasks.length ? Math.round((totalDone / tasks.length) * 100) : 0;

  return (
    <div className="screen-content fade-up">
      <div style={{background:'var(--navy)',padding:'1.5rem 1.25rem 2rem'}}>
        <div style={{fontSize:'0.68rem',letterSpacing:'0.15em',color:'rgba(250,247,242,0.4)',textTransform:'uppercase',marginBottom:'0.3rem'}}>Good day</div>
        <div style={{fontFamily:'var(--font-display)',fontSize:'1.5rem',fontWeight:300,color:'var(--cream)',marginBottom:'1.5rem'}}>
          {firstName}, <em style={{fontStyle:'italic',color:'var(--sage-light)'}}>let's plan</em> 💍
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'1.25rem',background:'rgba(250,247,242,0.05)',borderRadius:'var(--radius)',padding:'1.25rem'}}>
          <CountdownRing days={daysLeft ?? 0} totalDays={548}/>
          <div>
            <div style={{fontSize:'0.6rem',letterSpacing:'0.15em',textTransform:'uppercase',color:'rgba(250,247,242,0.4)',marginBottom:'0.15rem'}}>Days until your wedding</div>
            <div style={{fontFamily:'var(--font-display)',fontSize:'2.8rem',fontWeight:300,color:'var(--cream)',lineHeight:1}}>{daysLeft ?? '–'}</div>
            {weddingDate
              ? <div style={{fontSize:'0.78rem',fontWeight:300,color:'rgba(250,247,242,0.55)',marginTop:'0.2rem'}}>{format(weddingDate,'EEEE, d MMMM yyyy')}</div>
              : <div style={{fontSize:'0.78rem',fontWeight:300,color:'rgba(250,247,242,0.4)',marginTop:'0.2rem'}}>Set your date in Profile</div>
            }
          </div>
        </div>
      </div>

      <div style={{padding:'1.25rem'}}>
        <div className="section-label" style={{marginBottom:'0.85rem'}}>Your progress</div>
        <div className="card" style={{padding:'1.25rem'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
            <span style={{fontFamily:'var(--font-display)',fontSize:'1rem',fontWeight:400}}>Overall planning</span>
            <span style={{fontSize:'0.72rem',fontWeight:500,color:'var(--sage-dark)'}}>{overallPct}%</span>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{width:`${overallPct}%`}}/></div>
        </div>
      </div>

      <div style={{padding:'0 1.25rem'}}>
        <div className="section-label" style={{marginBottom:'0.85rem'}}>Your planning phases</div>
        {loading
          ? <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner"/></div>
          : tasks.length === 0
            ? <div style={{textAlign:'center',padding:'2rem',color:'var(--warm-grey)',fontSize:'0.88rem'}}>
                <p>No tasks yet — try signing out and back in.</p>
              </div>
            : phases.map(phase => (
                <TaskPhase key={phase.phase_number} phase={phase}
                  tasks={tasks.filter(t => t.phase_number === phase.phase_number)}
                  onToggle={handleToggle}/>
              ))
        }
      </div>

      {!isPremium && (
        <div style={{padding:'0 1.25rem 1.25rem'}}>
          <div className="upgrade-banner">
            <h3>Unlock <em>expert notes</em> at every stage</h3>
            <p>Upgrade to Premium for detailed guidance, budget tracking, vendor email scripts, and more.</p>
            <button className="btn btn-sage btn-full" onClick={() => window.location.href='/profile'}>
              Upgrade — from £12.99/mo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
